import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone, MapPin, Star, Wifi, Car, Utensils, Home, Shield, Check, MessageCircle, Calendar, Users, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast, Toaster } from "sonner";
import api from "@/lib/api";

const IMG = {
  hero: "https://images.unsplash.com/photo-1661332632427-83e4f7cd5d31?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
  room1: "https://images.unsplash.com/photo-1718894071402-fb944e2a1849?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  room2: "https://images.unsplash.com/photo-1747139157633-35b1ce94beb0?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  thali: "https://images.unsplash.com/photo-1589778655375-3e622a9fc91c?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  forest: "https://images.unsplash.com/photo-1723227129842-6784030d84fe?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
};

const WA_NUMBER = "918348009000";
const waLink = (msg) => `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;

export default function Landing() {
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", checkin: "", checkout: "", guests: 2, package_pref: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/campaigns").then((r) => setCampaigns(r.data || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.name || !form.phone) {
      toast.error("Please share your name and phone");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, source: "landing_page" });
      toast.success("Thank you! Pushpen-da or Parna-di will WhatsApp you shortly.");
      setForm({ name: "", phone: "", checkin: "", checkout: "", guests: 2, package_pref: "", notes: "" });
    } catch (e) {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#4A403B] font-body">
      <Toaster position="top-right" richColors />

      {/* Nav */}
      <nav className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-[#E7C2A6]/50" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-[#B84F3E] flex items-center justify-center text-white font-serif text-lg">M</div>
            <div>
              <div className="font-serif text-xl leading-none">Megh Balika</div>
              <div className="text-[10px] uppercase tracking-widest text-[#8A7970]">Santiniketan Homestay</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href={waLink("Hi, I want to know more about Megh Balika Homestay.")} target="_blank" rel="noreferrer" data-testid="nav-whatsapp-btn">
              <Button variant="outline" className="rounded-full border-[#B84F3E] text-[#B84F3E] hover:bg-[#F5F2EB] hidden sm:inline-flex">
                <MessageCircle className="w-4 h-4 mr-1.5" strokeWidth={1.5}/> WhatsApp
              </Button>
            </a>
            <a href="#enquire" data-testid="nav-enquire-btn">
              <Button className="rounded-full bg-[#B84F3E] text-white hover:bg-[#9A3F30]">Book Now</Button>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden" data-testid="landing-hero">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="Megh Balika Homestay" className="w-full h-full object-cover"/>
          <div className="absolute inset-0 hero-overlay"/>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-10 items-center">
          <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.8}} className="text-white">
            {campaigns[0] && (
              <div className="inline-flex items-center gap-2 mb-5 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/25 text-sm">
                <Sparkles className="w-4 h-4" strokeWidth={1.5}/>
                <span>{campaigns[0].tagline}</span>
              </div>
            )}
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl leading-none tracking-tight">Your peaceful <em className="italic text-[#F5E1A4]">Bengali home</em> in Santiniketan.</h1>
            <p className="mt-6 text-lg text-white/90 max-w-xl leading-relaxed">6 rooms only. Home-cooked Bengali meals. Free toto pickup. No GST when you book direct.</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href="#enquire"><Button className="rounded-full bg-[#B84F3E] hover:bg-[#9A3F30] text-white px-8 py-6 text-base" data-testid="hero-cta-book">Check Availability</Button></a>
              <a href={waLink("Hi, I'd like to book Megh Balika Homestay. Please help.")} target="_blank" rel="noreferrer">
                <Button variant="outline" className="rounded-full bg-white/10 backdrop-blur border-white/60 text-white hover:bg-white/20 px-8 py-6 text-base" data-testid="hero-cta-whatsapp">
                  <MessageCircle className="w-4 h-4 mr-2" strokeWidth={1.5}/> Chat on WhatsApp
                </Button>
              </a>
            </div>
            <div className="mt-10 flex items-center gap-6 text-white/85 text-sm">
              <div className="flex items-center gap-1.5"><Star className="w-4 h-4 fill-[#F5E1A4] text-[#F5E1A4]" strokeWidth={0}/><span>4.8 · 250+ Google reviews</span></div>
              <div className="hidden sm:flex items-center gap-1.5"><Home className="w-4 h-4" strokeWidth={1.5}/><span>6 exclusive rooms</span></div>
            </div>
          </motion.div>
          <div className="hidden md:block"/>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-[#FDFBF7] border-y border-[#E7C2A6]/40 py-6">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
          {[
            {icon: Check, label: "No GST · Direct booking"},
            {icon: Car, label: "Free Toto Pickup"},
            {icon: Utensils, label: "Home-cooked Bengali Food"},
            {icon: Wifi, label: "Fast WiFi in All Rooms"},
            {icon: Shield, label: "24hr CCTV · Family-run"},
          ].map((t, i) => (
            <div key={i} className="flex flex-col items-center gap-2">
              <t.icon className="w-5 h-5 text-[#B84F3E]" strokeWidth={1.5}/>
              <span className="text-xs sm:text-sm text-[#4A403B]">{t.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Rooms & Packages */}
      <section className="py-24 max-w-7xl mx-auto px-6" data-testid="rooms-section">
        <div className="mb-16 max-w-3xl">
          <p className="uppercase tracking-widest text-xs text-[#8A7970] mb-3">Rooms & Packages</p>
          <h2 className="font-serif text-4xl sm:text-5xl leading-tight">Two comfortable rooms.<br/><em className="italic text-[#B84F3E]">Three ways</em> to stay.</h2>
        </div>

        {/* Rooms */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {[
            {img: IMG.room1, name: "Deluxe Room", price: 1499, features: ["AC + Private Verandah", "Attached Bath · Geyser", "Free WiFi · Free Parking", "Sleeps 2 adults + 1 kid"]},
            {img: IMG.room2, name: "Super Deluxe Room", price: 1799, features: ["Larger AC Room + Verandah", "Attached Bath · Geyser", "Free WiFi · Free Parking", "Sleeps 2 adults + 1 kid"]},
          ].map((r, i) => (
            <motion.div key={i} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6, delay:i*0.15}} className="rounded-2xl overflow-hidden bg-white shadow-md border border-[#E7C2A6]/40" data-testid={`room-card-${i}`}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={r.img} alt={r.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"/>
              </div>
              <div className="p-7">
                <div className="flex justify-between items-start">
                  <h3 className="font-serif text-2xl">{r.name}</h3>
                  <div className="text-right">
                    <div className="text-xs text-[#8A7970]">from</div>
                    <div className="font-serif text-2xl text-[#B84F3E]">₹{r.price}<span className="text-sm text-[#8A7970]">/night</span></div>
                  </div>
                </div>
                <ul className="mt-4 space-y-1.5 text-sm text-[#4A403B]">
                  {r.features.map((f,j) => <li key={j} className="flex gap-2"><Check className="w-4 h-4 text-[#477A5D] shrink-0 mt-0.5" strokeWidth={2}/>{f}</li>)}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Packages */}
        <div className="mb-6">
          <h3 className="font-serif text-3xl mb-2">Add authentic Bengali food.</h3>
          <p className="text-[#8A7970] max-w-2xl">Fresh home-cooked meals prepared on pre-order. Per head, per night. Kids under 4 free · 4 to under 10 half price.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {name: "Room + Breakfast", price: "₹1,499 / ₹1,999", desc: "Per room/night · Deluxe / Super Deluxe · kids below 10 free", meals: ["Breakfast"], accent: false},
            {name: "Food + Stay — Flexi", price: "₹1,399", desc: "Per person/night · Deluxe room · 3 meals", meals: ["Breakfast", "Lunch", "Dinner"], accent: false},
            {name: "Food + Stay — Royal", price: "₹1,799", desc: "Per person/night · Super Deluxe room · 4 meals", meals: ["Breakfast", "Lunch", "Evening Snacks/Tea", "Dinner"], accent: true, badge: "Most Loved"},
          ].map((p, i) => (
            <div key={i} className={`rounded-2xl p-7 border ${p.accent ? "bg-[#B84F3E] text-white border-[#B84F3E]" : "bg-white border-[#E7C2A6]/40"}`} data-testid={`package-card-${i}`}>
              {p.badge && <div className="text-xs uppercase tracking-widest mb-3 opacity-90">🏆 {p.badge}</div>}
              <h4 className="font-serif text-2xl">{p.name}</h4>
              <div className="mt-3 flex items-baseline gap-2">
                <div className="font-serif text-3xl">{p.price}</div>
                {p.strike && <div className={`line-through text-sm ${p.accent ? "opacity-70" : "text-[#8A7970]"}`}>{p.strike}</div>}
              </div>
              <div className={`text-xs mt-1 ${p.accent ? "opacity-90" : "text-[#8A7970]"}`}>{p.desc}</div>
              {p.meals.length > 0 && (
                <ul className={`mt-5 space-y-1.5 text-sm ${p.accent ? "text-white/95" : ""}`}>
                  {p.meals.map((m,j) => <li key={j} className="flex gap-2"><Utensils className="w-3.5 h-3.5 shrink-0 mt-1" strokeWidth={1.5}/>{m}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Location */}
      <section className="py-24 bg-[#FDFBF7]" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <p className="uppercase tracking-widest text-xs text-[#8A7970] mb-3">Location</p>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight mb-6">In the heart of <em className="italic text-[#B84F3E]">Tagore's</em> Santiniketan.</h2>
            <p className="text-[#4A403B] leading-relaxed mb-8">Nestled in a quiet residential lane, minutes from Visva-Bharati, Sonajhuri Haat, and the red-soil landscape that inspired generations of Bengali art.</p>
            <div className="space-y-3">
              {[
                {place: "Prantik Railway Station", time: "3–4 min · Toto"},
                {place: "Bolpur Shantiniketan Station", time: "12–15 min · Toto"},
                {place: "Visva-Bharati University", time: "7–10 min · Toto"},
                {place: "Sonajhuri Haat", time: "7–10 min · Toto"},
                {place: "Kankalitala Mandir", time: "10–15 min · Toto"},
              ].map((l, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-[#E7C2A6]/40" data-testid={`location-${i}`}>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-[#B84F3E]" strokeWidth={1.5}/>
                    <span>{l.place}</span>
                  </div>
                  <span className="text-sm text-[#8A7970]">{l.time}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden">
              <img src={IMG.forest} alt="Santiniketan Red Soil" className="w-full h-full object-cover"/>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white p-5 rounded-2xl shadow-lg border border-[#E7C2A6]/40 max-w-xs hidden md:block">
              <div className="flex items-center gap-2 mb-2"><Star className="w-4 h-4 fill-[#D99B41] text-[#D99B41]" strokeWidth={0}/><Star className="w-4 h-4 fill-[#D99B41] text-[#D99B41]" strokeWidth={0}/><Star className="w-4 h-4 fill-[#D99B41] text-[#D99B41]" strokeWidth={0}/><Star className="w-4 h-4 fill-[#D99B41] text-[#D99B41]" strokeWidth={0}/><Star className="w-4 h-4 fill-[#D99B41] text-[#D99B41]" strokeWidth={0}/></div>
              <p className="text-xs italic text-[#4A403B]">"Best in class Family homestay in Santiniketan. Pushpen-da and Parna-di guided us throughout. High quality with 24hr CCTV, best for solo & group of girls too."</p>
              <p className="text-xs text-[#8A7970] mt-2">— Google Review · Feb 2026</p>
            </div>
          </div>
        </div>
      </section>

      {/* Enquire Form */}
      <section id="enquire" className="py-24 max-w-4xl mx-auto px-6" data-testid="enquire-section">
        <div className="text-center mb-10">
          <p className="uppercase tracking-widest text-xs text-[#8A7970] mb-3">Check Availability</p>
          <h2 className="font-serif text-4xl sm:text-5xl leading-tight">Direct-booking guests receive the <em className="italic text-[#B84F3E]">warmest welcome</em>.</h2>
          <p className="mt-4 text-[#8A7970]">No GST · Fixed advance ₹1,000/₹2,000 · Free breakfast · Toto pickup available</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E7C2A6]/50 p-8 shadow-sm">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-[#8A7970] mb-1.5 block">Your Name *</label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Full Name" data-testid="enquire-name-input"/>
            </div>
            <div>
              <label className="text-sm text-[#8A7970] mb-1.5 block">WhatsApp Number *</label>
              <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="+91 ..." data-testid="enquire-phone-input"/>
            </div>
            <div>
              <label className="text-sm text-[#8A7970] mb-1.5 block">Check-in Date</label>
              <Input type="date" value={form.checkin} onChange={(e) => setForm({...form, checkin: e.target.value})} data-testid="enquire-checkin-input"/>
            </div>
            <div>
              <label className="text-sm text-[#8A7970] mb-1.5 block">Check-out Date</label>
              <Input type="date" value={form.checkout} onChange={(e) => setForm({...form, checkout: e.target.value})} data-testid="enquire-checkout-input"/>
            </div>
            <div>
              <label className="text-sm text-[#8A7970] mb-1.5 block">Number of Guests</label>
              <Input type="number" min={1} value={form.guests} onChange={(e) => setForm({...form, guests: parseInt(e.target.value)||1})} data-testid="enquire-guests-input"/>
            </div>
            <div>
              <label className="text-sm text-[#8A7970] mb-1.5 block">Package Preference</label>
              <select className="w-full h-10 rounded-md border border-[#E7C2A6] bg-white px-3" value={form.package_pref} onChange={(e) => setForm({...form, package_pref: e.target.value})} data-testid="enquire-package-select">
                <option value="">Not sure yet</option>
                <option value="room_breakfast">Room + Breakfast</option>
                <option value="flexi">Food + Stay — Flexi (3 meals)</option>
                <option value="royal">Food + Stay — Royal (4 meals)</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm text-[#8A7970] mb-1.5 block">Anything special? (optional)</label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="e.g., anniversary trip, senior guests, dietary preferences..." data-testid="enquire-notes-input"/>
            </div>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button onClick={submit} disabled={submitting} className="rounded-full bg-[#B84F3E] hover:bg-[#9A3F30] text-white py-6 px-8 flex-1" data-testid="enquire-submit-btn">
              {submitting ? "Sending..." : "Request Callback"}
            </Button>
            <a href={waLink(`Hi, I'd like to book Megh Balika. Name: ${form.name}, Dates: ${form.checkin} to ${form.checkout}, Guests: ${form.guests}`)} target="_blank" rel="noreferrer" className="flex-1">
              <Button variant="outline" className="rounded-full border-[#B84F3E] text-[#B84F3E] hover:bg-[#F5F2EB] py-6 px-8 w-full" data-testid="enquire-whatsapp-btn">
                <MessageCircle className="w-4 h-4 mr-2" strokeWidth={1.5}/> WhatsApp Us Instead
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#4A403B] text-white/80 py-14" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-10">
          <div>
            <div className="font-serif text-2xl text-white">Megh Balika</div>
            <div className="text-xs uppercase tracking-widest opacity-70 mt-1">Santiniketan Family Homestay</div>
            <p className="text-sm mt-4 leading-relaxed opacity-80">A peaceful family homestay near Sonajhuri & Visva-Bharati, run by Pushpen-da & Parna-di since 2024.</p>
          </div>
          <div>
            <div className="text-sm uppercase tracking-widest opacity-70 mb-4">Contact</div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2"><Phone className="w-4 h-4" strokeWidth={1.5}/>+91 8348009000</div>
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4" strokeWidth={1.5}/>Santiniketan, Bolpur, West Bengal</div>
              <a href="https://mbhomestay.in" className="flex items-center gap-2 hover:text-white"><Home className="w-4 h-4" strokeWidth={1.5}/>mbhomestay.in</a>
            </div>
          </div>
          <div>
            <div className="text-sm uppercase tracking-widest opacity-70 mb-4">Direct Booking Perks</div>
            <ul className="text-sm space-y-1.5">
              <li>· No GST or tax</li>
              <li>· Small fixed advance (₹1,000 / ₹2,000)</li>
              <li>· Free toto pickup</li>
              <li>· Complimentary breakfast</li>
              <li>· Host confirms personally</li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 mt-10 pt-6 border-t border-white/10 text-xs opacity-60 text-center">
          © 2026 Megh Balika Homestay. Made with warmth in Santiniketan.
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a href={waLink("Hi Megh Balika, I want to enquire about a stay.")} target="_blank" rel="noreferrer" className="fixed bottom-6 right-6 z-50" data-testid="floating-whatsapp-btn">
        <div className="w-14 h-14 rounded-full bg-[#25D366] shadow-lg flex items-center justify-center text-white hover:scale-110 transition-transform">
          <MessageCircle className="w-6 h-6" strokeWidth={1.8}/>
        </div>
      </a>
    </div>
  );
}
